import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';

interface CommentFormProps {
  onSubmit: (text: string) => Promise<boolean>;
  onCancel?: () => void;
  placeholder?: string;
  submitLabel?: string;
  className?: string;
}

/**
 * CommentForm - Text input for posting comments and replies
 * Features:
 * - Character limit (2000 chars)
 * - Character counter
 * - Submit/Cancel buttons
 * - Loading state
 */
export const CommentForm: React.FC<CommentFormProps> = ({
  onSubmit,
  onCancel,
  placeholder = 'Share your thoughts...',
  submitLabel = 'Post',
  className
}) => {
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmedText = text.trim();
    if (!trimmedText || trimmedText.length > 2000) return;

    setIsSubmitting(true);
    const success = await onSubmit(trimmedText);

    if (success) {
      setText(''); // Clear form on success
    }

    setIsSubmitting(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Ctrl/Cmd + Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isValid = text.trim().length > 0 && text.length <= 2000;

  return (
    <div className={cn('space-y-2', className)}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={cn(
          'w-full min-h-[100px] p-3 rounded-md',
          'border border-border bg-background text-foreground',
          'placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
          'resize-y',
          'transition-colors'
        )}
        maxLength={2000}
        disabled={isSubmitting}
      />

      <div className="flex justify-between items-center">
        <span
          className={cn(
            'text-xs',
            text.length > 2000
              ? 'text-destructive font-semibold'
              : text.length > 1800
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-muted-foreground'
          )}
        >
          {text.length}/2000
        </span>

        <div className="flex gap-2">
          {onCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}

          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="min-w-[80px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Posting...
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </div>

      {text.trim() && (
        <p className="text-xs text-muted-foreground">
          Tip: Press Ctrl+Enter to submit quickly
        </p>
      )}
    </div>
  );
};
